import { useCallback, useEffect, useRef, useState } from 'react';
import { claveIdempotencia } from '../../api/comun';
import type { Operaciones } from '../../api/operaciones';
import type { Afectado, PeticionVistaPrevia, ProblemaAfectados } from '../../api/ops/disponibilidad';
import { ErrorApi } from '../../api/problema';
import type { MostrarAviso } from '../../componentes/avisoBreve';
import type { NombreIcono } from '../../componentes/iconos';
import { useEnvioRetrasado } from '../../logica/envioRetrasado';
import { avisoBreveDeError } from '../../logica/estados';
import { textoConfirmado, type Palabras } from './textos';

/**
 * Cómo se guarda cualquier cambio del horario (semana, día especial, festivo, copia, zona, anular o quitar; G250):
 *
 * 1. `previewScheduleChange` con el cambio tal cual.
 * 2. Sin afectados, el envío se retrasa 5 s con «Deshacer» (G159, `logica/envioRetrasado.ts`).
 * 3. Con afectados, la ventana 3.4; «Confirmar el cambio» manda la operación con el `change_id`, sin «Deshacer».
 *
 * La operación del cambio puede responder `409 commitments_pending` (alguien reservó entre medias): se pinta igual que la
 * vista previa, con su `change_id`. Con la vista previa caducada (15 minutos o el horario cambió), `409 change_expired`
 * (el `code` del `Problem`, G856): la ventana pasa a «Calcular otra vez».
 */
export interface Cambio {
    /** Qué cambio es, en la ventana: «Semana tipo de todo el negocio · desde el miércoles 14 de octubre». */
    contexto: string;
    icono: NombreIcono;
    /** Cambia el calendario del negocio: no hay otro sitio abierto (G254). */
    todoElNegocio: boolean;
    /** Cambio de zona horaria: la ventana explica que las reservas guardan su hora. */
    zona?: boolean;
    peticion: PeticionVistaPrevia;
    /** La operación del cambio; con afectados, con el `change_id`. */
    aplicar: (changeId: string | undefined, clave: string) => Promise<void>;
    /** El aviso con «Deshacer» cuando no hay afectados. */
    textoDeshacer: string;
    /** Lo que se ve mientras tanto (el cambio ya hecho) y cómo se vuelve atrás si se deshace o falla. */
    alProgramar?: () => void;
    alDeshacer?: () => void;
    /** Ya guardado: se vuelve a leer. */
    alGuardar: () => void;
    /** Un error del cambio que pinta la pantalla (los 422 bajo su tramo); true si lo pintó. */
    alFallar?: (e: unknown) => boolean;
}

export interface VistaImpacto {
    change_id: string;
    expires_at?: string;
    affected_count: number;
    affected_holds: Afectado[];
}

export interface Impacto {
    cambio: Cambio;
    vista: VistaImpacto;
    caducada: boolean;
    ocupado: boolean;
}

/** ¿La vista previa caducó (15 minutos) o el horario cambió entre medias? */
export function esCaducada(e: unknown): boolean {
    return e instanceof ErrorApi && e.codigo === 'change_expired';
}

/** `409 commitments_pending` de Disponibilidad, con su `change_id` y los afectados (`AffectedHoldsProblem`). */
export function afectadosDe(e: unknown): VistaImpacto | null {
    if (!(e instanceof ErrorApi) || e.codigo !== 'commitments_pending') {
        return null;
    }
    const p = e.problema as unknown as ProblemaAfectados;
    if (p.change_id === undefined) {
        return null;
    }
    const holds = p.affected_holds ?? [];
    const total = (p.commitments ?? []).reduce((n, c) => n + c.count, 0);
    return { change_id: p.change_id, affected_count: total > 0 ? total : holds.length, affected_holds: holds };
}

export function textoDeErrorApi(e: unknown, porDefecto: string): string {
    return avisoBreveDeError(e) ?? (e instanceof ErrorApi ? (e.problema.detail ?? e.problema.title) : porDefecto);
}

export function useCambios({ ops, negocio, avisar, palabras }: { ops: Operaciones; negocio: string; avisar: MostrarAviso; palabras: Palabras }) {
    const envio = useEnvioRetrasado();
    const programarEnvio = envio.programar;
    const [impacto, setImpacto] = useState<Impacto | null>(null);
    const [calculando, setCalculando] = useState(false);
    const [hecho, setHecho] = useState<string | null>(null);
    const vivo = useRef(true);
    useEffect(() => {
        vivo.current = true;
        return () => {
            vivo.current = false;
        };
    }, []);

    const fallo = useCallback(
        (cambio: Cambio, e: unknown) => {
            const conAfectados = afectadosDe(e);
            if (conAfectados !== null) {
                setImpacto({ cambio, vista: conAfectados, caducada: false, ocupado: false });
                return;
            }
            if (cambio.alFallar?.(e) === true) {
                return;
            }
            avisar({ tipo: 'error', texto: textoDeErrorApi(e, 'No he podido guardar el cambio. Prueba de nuevo.') });
        },
        [avisar],
    );

    const programar = useCallback(
        (cambio: Cambio) => {
            const clave = claveIdempotencia();
            cambio.alProgramar?.();
            programarEnvio({
                texto: cambio.textoDeshacer,
                donde: 'horario',
                enviar: () => {
                    cambio
                        .aplicar(undefined, clave)
                        .then(() => {
                            cambio.alGuardar();
                        })
                        .catch((e: unknown) => {
                            cambio.alDeshacer?.();
                            fallo(cambio, e);
                        });
                },
                alDeshacer: cambio.alDeshacer,
            });
        },
        [programarEnvio, fallo],
    );

    /** Pide la vista previa y sigue según haya afectados o no. */
    const pedir = useCallback(
        async (cambio: Cambio) => {
            setHecho(null);
            setCalculando(true);
            try {
                const vista = await ops.previewScheduleChange(negocio, cambio.peticion);
                if (!vivo.current) {
                    return;
                }
                if (vista.affected_count > 0) {
                    setImpacto({ cambio, vista, caducada: false, ocupado: false });
                } else {
                    programar(cambio);
                }
            } catch (e) {
                fallo(cambio, e);
            } finally {
                if (vivo.current) {
                    setCalculando(false);
                }
            }
        },
        [ops, negocio, programar, fallo],
    );

    const recalcular = useCallback(
        async (actual: Impacto) => {
            setImpacto({ ...actual, ocupado: true });
            try {
                const vista = await ops.previewScheduleChange(negocio, actual.cambio.peticion);
                if (vista.affected_count > 0) {
                    setImpacto({ cambio: actual.cambio, vista, caducada: false, ocupado: false });
                } else {
                    setImpacto(null);
                    programar(actual.cambio);
                }
            } catch (e) {
                setImpacto(null);
                fallo(actual.cambio, e);
            }
        },
        [ops, negocio, programar, fallo],
    );

    /** «Confirmar el cambio» (o «Calcular otra vez» si caducó). */
    const confirmar = useCallback(async () => {
        const actual = impacto;
        if (actual === null || actual.ocupado) {
            return;
        }
        if (actual.caducada) {
            await recalcular(actual);
            return;
        }
        setImpacto({ ...actual, ocupado: true });
        try {
            await actual.cambio.aplicar(actual.vista.change_id, claveIdempotencia());
            setImpacto(null);
            setHecho(textoConfirmado(actual.vista.affected_count, actual.cambio.todoElNegocio, palabras));
            actual.cambio.alGuardar();
        } catch (e) {
            if (esCaducada(e)) {
                setImpacto({ ...actual, caducada: true, ocupado: false });
                return;
            }
            const otra = afectadosDe(e);
            if (otra !== null) {
                setImpacto({ cambio: actual.cambio, vista: otra, caducada: false, ocupado: false });
                return;
            }
            setImpacto(null);
            fallo(actual.cambio, e);
        }
    }, [impacto, recalcular, palabras, fallo]);

    // La vista previa vale 15 minutos: al pasar su `expires_at`, la ventana se pone en «Calcular otra vez».
    const caduca = impacto !== null && !impacto.caducada ? impacto.vista.expires_at : undefined;
    useEffect(() => {
        if (caduca === undefined) {
            return;
        }
        const falta = new Date(caduca).getTime() - Date.now();
        if (Number.isNaN(falta)) {
            return;
        }
        const t = setTimeout(() => {
            setImpacto((i) => (i === null ? i : { ...i, caducada: true }));
        }, Math.max(0, falta));
        return () => {
            clearTimeout(t);
        };
    }, [caduca]);

    const cerrarImpacto = useCallback(() => {
        setImpacto((i) => (i?.ocupado === true ? i : null));
    }, []);

    return { envio, impacto, calculando, hecho, setHecho, pedir, confirmar, cerrarImpacto };
}
